import Image from 'components/Image'
import React from 'react'
import CtaLink from 'components/ui/CtaLink'
import useInfoDialog from 'hooks/useInfoDialog'
import { InformationCircleIcon } from '@heroicons/react/outline'
import HERO_IMAGE from 'images/party-2-small.jpg'
import { gql } from '@apollo/client'
import { marketHeroImageFragment as marketHeroImageFragmentType } from 'components/MarketHeroSection/types/marketHeroImageFragment'
import { ClientLogos } from 'components/ClientClouds'
import ButtonLink from 'components/ui/ButtonLink'
import cn from 'classnames'

export const marketHeroImageFragment = gql`
  fragment marketHeroImageFragment on Market {
    id
    name
    image {
      original
      medium
      present
    }
    imageAuthor
    imageAuthorUrl
    imageLicense
  }
`

type Props = {
  state: string
  market: marketHeroImageFragmentType
  isCateringPage?: boolean
  slug: string
}

const HeroSection = ({
  state,
  market,
  isCateringPage = false,
  slug
}: Props) => {
  const infoDialog = useInfoDialog()
  const onShowAttribution = () => {
    infoDialog({
      title: 'Photo Information',
      content: (
        <div>
          {market.imageAuthor && (
            <div>
              {market.imageAuthorUrl ? (
                <>
                  Photo by{' '}
                  <a
                    className="underline text-blue-500"
                    href={market.imageAuthorUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {market.imageAuthor}
                  </a>
                </>
              ) : (
                <span>Photo by {market.imageAuthor}</span>
              )}
            </div>
          )}
          {market.imageLicense && (
            <div>
              License: <span>{market.imageLicense}</span>
            </div>
          )}
        </div>
      )
    })
  }

  return (
    <div>
      <main className="lg:relative">
        <div className="mx-auto max-w-7xl w-full pt-16 pb-10 md:pb-20 text-center lg:py-48 lg:text-left">
          <div className="px-4 lg:w-1/2 sm:px-8 xl:pr-16">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl">
              <span className="block xl:inline">
                {isCateringPage ? 'Food Truck Catering in' : 'Food Trucks in'}
              </span>{' '}
              <span className="block text-orange-500 xl:inline">
                {market.name}, {state}
              </span>
            </h1>
            <div className="mt-4 w-80 md:w-96 relative flex flex-col items-center gap-y-4 mx-auto lg:mx-0">
              <div className={cn('mx-auto', { 'mb-8': isCateringPage })}>
                {isCateringPage ? (
                  <p className="max-w-md mx-auto text-lg text-gray-700 sm:text-xl md:mt-5 md:max-w-3xl">
                    Ditch the old style of catering for your next event and book
                    gourmet food trucks instead.
                  </p>
                ) : (
                  <div className="w-80 md:w-96">
                    <p className="text-center pt-10 max-w-md mx-auto text-lg text-gray-700 sm:text-xl md:mt-5 md:max-w-3xl font-semibold">
                      Order online from our trucks below
                    </p>
                    <div className="flex items-center my-2">
                      <div className="w-5/12 border-t border-solid border-gray-400" />
                      <div className="w-2/12 text-center italic text-gray-400">
                        or
                      </div>
                      <div className="w-5/12 border-t border-solid border-gray-400" />
                    </div>
                  </div>
                )}
              </div>

              <CtaLink href="/hire-food-truck" prefetch={false} fullWidth>
                <p className="whitespace-nowrap">
                  Hire a food truck in {market.name}
                </p>
              </CtaLink>
              {!isCateringPage && (
                <ButtonLink
                  color="secondary"
                  href={`/food-trucks/catering/${slug}`}
                  variant="contained"
                  fullWidth
                >
                  Learn about {market.name} catering
                </ButtonLink>
              )}
            </div>
          </div>
        </div>
        <div className="relative w-full h-64 sm:h-72 md:h-96 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 lg:h-full">
          <Image
            className="absolute inset-0 w-full h-full object-cover object-center"
            crop="fill"
            gravity="auto"
            height={800}
            layout="fill"
            sizes="(max-width: 768px) 100vw,50vw"
            src={market.image.present ? market.image.original! : HERO_IMAGE}
            width={1200}
            priority
          />
          {(market.imageAuthor || market.imageLicense) && (
            <div className="absolute bottom-1 right-2">
              <a
                className="text-gray-800 font-light underline text-sm flex items-center gap-1 bg-gray-400 bg-opacity-50 rounded-md px-1"
                href="#"
                onClick={() => onShowAttribution()}
              >
                <InformationCircleIcon className="h-4 w-4" />
                Photo info
              </a>
            </div>
          )}
        </div>
      </main>
      <ClientLogos
        className="bg-white border-t-2 border-b-2 border-solid border-orange-300"
        clients={[
          'pfizer',
          'boeing',
          'apple',
          'amazon',
          'cushman',
          'aramark',
          'compass',
          'swedish'
        ]}
      />
    </div>
  )
}

export default HeroSection
